import React from 'react';
import SlideBanner from '@components/common/SlideBanner/SlideBanner';
import JobHome from '@components/Job/JobHome';
import JobNav from '@components/Job/JobNav';
import {NextSeo} from 'next-seo';
import RecruitmentService from '@services/recruitment.service';
import RecruitmentCategoryService from '@services/recruitment-category.service';
import RecruitmentNewsService from '@services/recruitment-news.service';
import {generateRecruitmentListSEO} from '@helper/seo/generators/recruitment';
import {getLocaleContent} from '@helper/locale-content.js';
import {useRouter} from 'next/router';

export default function JobListingPage(props) {
    const {seoPage} = props;
    const router = useRouter();
    const {locale} = router;
    return (
        <>
            {seoPage && <NextSeo {...seoPage} />}
            <div>
                <SlideBanner titleBanner={getLocaleContent(locale, 'page.recruitment.title_banner')} />
                <JobNav />
                <JobHome {...props} />
            </div>
        </>
    );
}

JobListingPage.PageName = 'JOB';

JobListingPage.getInitialProps = async context => {
    try {
        const ctx = context || {};
        const webUrl = process.env.NEXT_PUBLIC_WEB_URL || process.env.WEB_URL || 'https://tnh.com.vn';

        console.log('[JobListing] Start getInitialProps', {locale: ctx.locale, query: ctx.query});

        let recruitments = [];
        let totalCount = 0;

        let categories = [];
        let newsByCategory = {};

        // SERVICES
        const recruitmentService = new RecruitmentService(ctx);
        const recruitmentCategoryService = new RecruitmentCategoryService(ctx);
        const recruitmentNewsService = new RecruitmentNewsService(ctx);

        // ===============================
        // 1) FETCH SONG SONG (recruitment + category)
        // ===============================
        console.log('[JobListing] Fetching recruitment list and category list in parallel...');
        let [recruitmentRes, categoryRes] = await Promise.all([
            recruitmentService
                .getRecruitmentList({
                    locale: ctx.locale || 'vn',
                    pageSize: 20,
                    pageIndex: 1,
                })
                .then(res => {
                    console.log('[JobListing] recruitmentRes received', res && (res.data ?? res));
                    return res;
                })
                .catch(e => {
                    console.error('[JobListing] Recruitment error:', e);
                    return null;
                }),

            recruitmentCategoryService
                .getRecruitmentCategoryOptions({
                    locale: ctx.locale || 'vn',
                })
                .then(res => {
                    console.log('[JobListing] categoryRes received', res && (res.data ?? res));
                    return res;
                })
                .catch(e => {
                    console.error('[JobListing] Category error:', e);
                    return null;
                }),
        ]);

        // ===============================
        // 2) PARSE CATEGORY
        // ===============================
        try {
            console.log('[JobListing] Parsing categoryRes...');
            if (categoryRes) {
                let payload = categoryRes.data?.data || categoryRes.data || categoryRes;
                console.log('[JobListing] raw category payload:', payload);
                if (Array.isArray(payload)) {
                    categories = payload;
                } else if (payload.items && Array.isArray(payload.items)) {
                    categories = payload.items;
                }
            }
            console.log('[JobListing] Parsed categories count:', categories.length);
        } catch (e) {
            console.error('[JobListing] Parse category error:', e);
        }

        // ===============================
        // 3) LẤY NEWS CHO TẤT CẢ CATEGORY
        // ===============================
        try {
            console.log(
                '[JobListing] Starting to fetch news for each category... total categories:',
                categories.length,
            );

            const newsPromises = categories.map(cat => {
                const catId = cat.category_id ?? null;
                //console.log(`[JobListing] Queue news fetch for category_id=${JSON.stringify(catId)}`);

                return recruitmentNewsService
                    .getNewsByCategory({
                        locale: ctx.locale || 'vn',
                        page: 1,
                        page_size: 20,
                        id: Number(catId),
                    })
                    .then(res => {
                        let payload = res?.data?.data || res?.data || res || [];
                        console.log(`[JobListing] news response for category ${catId}:`, payload);
                        newsByCategory[catId] = Array.isArray(payload) ? payload : payload.items || [];
                    })
                    .catch(e => {
                        console.error(`[JobListing] News error for category ${catId}:`, e);
                        newsByCategory[catId] = [];
                    });
            });

            await Promise.all(newsPromises);
            console.log(
                '[JobListing] All news fetched for categories. newsByCategory keys:',
                Object.keys(newsByCategory),
            );
        } catch (e) {
            console.error('[JobListing] News fetch error:', e);
        }

        // ===============================
        // 4) PARSE RECRUITMENTS
        // ===============================
        try {
            console.log('[JobListing] Parsing recruitmentRes...');
            if (recruitmentRes) {
                let payload = recruitmentRes.data?.data || recruitmentRes.data || recruitmentRes;

                totalCount = parseInt(payload.totalItems || payload.totalCount || 0) || 0;

                if (Array.isArray(payload)) {
                    recruitments = payload;
                } else if (Array.isArray(payload.items)) {
                    recruitments = payload.items;
                } else if (Array.isArray(payload.data)) {
                    recruitments = payload.data;
                }
            }
            console.log('[JobListing] Parsed recruitments count:', recruitments.length, 'totalCount:', totalCount);
        } catch (e) {
            console.error('[JobListing] Parse recruitment error:', e);
        }

        // ===============================
        // 5) SEO
        // ===============================
        console.log('[JobListing] Generating SEO page with recruitment and category/news data...');
        const seoPage = generateRecruitmentListSEO(recruitments, totalCount, webUrl, categories, newsByCategory);
        console.log('[JobListing] SEO generated:', seoPage && {title: seoPage.title, description: seoPage.description});

        const result = {seoPage, categories, newsByCategory};

        return result;
    } catch (error) {
        console.error('[JobListing] Failed to fetch SEO for job listing page:', error);

        const webUrl = process.env.NEXT_PUBLIC_WEB_URL || process.env.WEB_URL || 'https://tnh.com.vn';
        const seoPage = generateRecruitmentListSEO([], 0, webUrl);
        console.log('[JobListing] Returning fallback SEO and empty data');

        return {seoPage, categories: [], newsByCategory: {}};
    }
};
